// Platform seams for the shared surfaces — the ONE confirm/notify contract.
//
// A shared component can't reach a platform dialog itself: react-native's Alert
// is a no-op on react-native-web, and window.confirm doesn't exist on native. So
// the mechanism is injected by the host mount and only the mechanism — the copy
// is resolved by the caller (shared i18n), never by the seam.
//
// This module exists because the contract was written TWICE — ConfirmFn in
// Maxes/useMaxEditor and MeasurementConfirmFn in Settings/measurements, byte-
// identical but for the field names (confirmLabel/cancelLabel vs confirmText/
// cancelText) — which forced four near-identical implementations across the two
// hosts. CO-7's coach target editor would have been the third type and the fifth
// and sixth implementations; instead the concept lives here once, and each host
// implements it exactly once. The `*Label` naming wins over `*Text` to match the
// primitives layer (PageSheet.cancelLabel).
//
// Both seams are CONCEPT-neutral on purpose: nothing here knows about MAXes,
// measurements or targets. A new surface needing a confirm imports this type and
// takes the host's existing implementation — it does not write a fifth one.

/** Platform-supplied confirmation for a destructive action. Resolves false on
 *  cancel AND on dismiss (tap-out / back), so a dismissed dialog is never read
 *  as consent. */
export type ConfirmFn = (opts: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  /**
   * Does saying yes DESTROY something? Defaults to true, because until RW-25
   * every caller was a delete/discard/clear and the native implementation
   * hard-coded `style: 'destructive'` for all of them.
   *
   * `useBodyweightPrompt` is the first caller that asks a non-destructive
   * question — "Set it" opens Settings → Body — and inherited the red
   * treatment meant for "Delete". Colour is the only thing this changes:
   * an invitation must not be dressed as a demolition.
   *
   * ⚠️ Invisible on Android TODAY (react-native's Alert button `style` is
   * iOS-only) and on the web (window.confirm has no styling at all), so no
   * screenshot will show the difference on the family's phones. It is in the
   * contract anyway: the flag records what the question IS, and the next
   * platform to render it honestly gets it right for free.
   */
  destructive?: boolean;
}) => Promise<boolean>;

/** Platform-supplied one-way message (no choice to make, nothing to await). */
export type NotifyFn = (title: string, body: string) => void;

/** What became of a write. A caller that ignores this re-creates the bug the
 *  whole of DX-2 exists to kill: a write that fails and says nothing. */
export type WriteOutcome = "sent" | "queued" | "rejected";

/**
 * Platform-supplied DURABLE WRITE — the third seam, and it exists for the same
 * reason as the two above: the shared core cannot reach the mechanism. The
 * offline queue lives in `apps/mobile`, so a hook in this package physically
 * cannot decide "network first, queue on failure" on its own.
 *
 * Concept-neutral like its siblings: it knows what a request is, not what a
 * bookmark is.
 *
 * ⚠️ The two hosts do NOT promise the same thing TODAY, and until 2026-08-14
 * that asymmetry was itself the decision (Antenore, 2026-08-12):
 *  - **mobile** routes through `writeOrQueue` onto the request queue, so
 *    `queued` genuinely means durable — it survives the app being killed.
 *  - **web-rnw** has no queue at all. It sends, and reports `rejected` when the
 *    write does not land. It must never answer `queued`: claiming a durability
 *    that does not exist is worse than today's silence, because the athlete
 *    would stop worrying about a write that is already gone.
 *
 * ⇒ **SUPERSEDED 2026-08-14 (Antenore): full mobile↔web-rnw parity, and the
 * only thing that stays off a surface is what that surface CANNOT host.** A
 * browser can hold a durable queue (IndexedDB + service worker), so "web has
 * no queue" is no longer a decision — it is debt, and it is in the lift pool
 * (`offline-sync--generic-offline-outbox-engine`, `pnpm lift:pool`).
 *
 * ⛔ What has NOT changed, and must not: **`rejected` stays the honest answer
 * until a real queue exists here.** The contract above is about what web may
 * CLAIM, and answering `queued` without durability is still the worst option.
 * This seam flips only when the queue is actually built — never before.
 * ⚠️ And that build is on the family's write path: it is not nightly-agent
 * work ([[NEXT.md §The one durable rule]]).
 *
 * No `id` in the payload: the idempotency key is queue bookkeeping, not a
 * business decision, so each host mints its own (mobile: before enqueueing;
 * web: never needed, `apiFetch` stamps a fresh one per call). A caller has
 * no domain reason to control it — `requestOutbox` doesn't dedupe by entity
 * (offline-writes.md §IL SEAM DI SCRITTURA), so there is nothing to key by
 * except "this one attempt".
 *
 * The result carries the raw error on `rejected` because callers map the
 * status to their own copy (`useMaxEditor` and `useWorkoutDelete` both
 * render `api_error_<status>`); a bare outcome string would have flattened
 * every deterministic 4xx into one generic message.
 *
 * ⛔ Two shapes of write stay OFF this seam, and both for the same underlying
 * reason — a queued write has no conversation, only a delivery:
 *  - **Optimistic locks.** `useTargetsDoc` PATCHes with a required `If-Match`
 *    and reads the response ETag back as the next save's validator. A replay
 *    would arrive validator-less and take a guaranteed 428
 *    (`apps/api/src/routes/meTargets.ts:131`). A write that is only valid
 *    against the version it read cannot be made durable by replaying it.
 *  - **Writes whose caller needs the RESPONSE.** `useFeedbackInbox` parses
 *    the server's updated row to move its local counters. There is no
 *    response to hand back at enqueue time, so such a caller is by
 *    construction an online-only one.
 * Both stay online-only, like `DELETE /api/me`.
 */
export type DurableWriteResult =
  | { outcome: "sent" | "queued" }
  | { outcome: "rejected"; error: unknown };

export type DurableWriteFn = (op: {
  method: "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  body?: unknown;
}) => Promise<DurableWriteResult>;

/**
 * The `Error | null` reading of a write result, for the hooks that hand an
 * error back to their caller instead of rendering it (usePlans, usePrograms,
 * useLibrary, useFavorites — the shape predates the seam).
 *
 * ⭐ `queued` reads as null, i.e. as success, and that is the whole point:
 * on mobile a queued write is durable, so surfacing an error for it would
 * be the lie DX-2 exists to remove. Web never answers `queued`.
 */
export const writeError = (result: DurableWriteResult): Error | null => {
  if (result.outcome !== "rejected") return null;
  return result.error instanceof Error
    ? result.error
    : new Error(String(result.error));
};

/**
 * The `api_error_<status>` reading of a rejection, for the surfaces that
 * render that code (the app-wide convention). Null when the write is not
 * rejected, and null when no server answered — a status only exists when
 * one did, and an unreachable network is not a verdict
 * ([[feedback_auth_failure_is_not_a_verdict]]). The caller supplies its own
 * copy for that case, because only it knows what the athlete was doing.
 */
export const writeErrorCode = (result: DurableWriteResult): string | null => {
  if (result.outcome !== "rejected") return null;
  const status = (result.error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? `api_error_${status}` : null;
};
